import { useState, useEffect } from 'react';
import { collection, query, where, onSnapshot, addDoc, doc, getDocs, updateDoc, deleteDoc, arrayUnion, arrayRemove } from 'firebase/firestore';
import { db, auth } from '../services/firebase';
import { useOrganization } from './useOrganization';

export function useTeams() {
  const { orgMember } = useOrganization();
  const [teams, setTeams] = useState([]);
  const [currentTeam, setCurrentTeam] = useState(null);
  const [joinRequests, setJoinRequests] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!orgMember || !orgMember.orgId) {
      setTeams([]);
      setCurrentTeam(null);
      setJoinRequests([]);
      setLoading(false);
      return;
    }

    // Fetch all teams for the organization
    const q = query(collection(db, 'teams'), where('orgId', '==', orgMember.orgId));
    
    const unsubscribeDb = onSnapshot(q, (snapshot) => {
      const fetchedTeams = snapshot.docs.map(d => ({
        id: d.id,
        ...d.data()
      }));
      setTeams(fetchedTeams);
      
      // Determine currentTeam
      if (orgMember.teamIds && orgMember.teamIds.length > 0) {
        const matchingTeam = fetchedTeams.find(t => orgMember.teamIds.includes(t.id));
        setCurrentTeam(matchingTeam || null);
      } else {
        setCurrentTeam(null);
      }
      
      setLoading(false);
    }, (err) => {
      console.error("Teams query error:", err);
      setLoading(false);
    });

    // Fetch team join requests if they are an admin or a team lead
    // For simplicity, we just fetch them for the org
    const reqQ = query(collection(db, 'team_requests'), where('orgId', '==', orgMember.orgId), where('status', '==', 'pending'));
    const unsubscribeReqs = onSnapshot(reqQ, (snapshot) => {
      setJoinRequests(snapshot.docs.map(d => ({ id: d.id, ...d.data() })));
    });

    return () => {
      unsubscribeDb();
      unsubscribeReqs();
    };
  }, [orgMember]);

  const createTeam = async (name) => {
    if (!auth.currentUser || !orgMember || (orgMember.role !== 'admin' && orgMember.role !== 'Administrator')) throw new Error("Must be an org admin");
    
    const docRef = await addDoc(collection(db, 'teams'), {
      name,
      orgId: orgMember.orgId,
      createdBy: auth.currentUser.uid,
      createdAt: new Date().toISOString()
    });
    return docRef.id;
  };

  const addUserToTeam = async (teamId, userId) => {
    if (!auth.currentUser || !orgMember || (orgMember.role !== 'admin' && orgMember.role !== 'Administrator')) throw new Error("Must be an org admin");
    const memberId = `${userId}_${orgMember.orgId}`;
    await updateDoc(doc(db, 'org_members', memberId), {
      teamIds: arrayUnion(teamId)
    });
  };

  const removeUserFromTeam = async (teamId, userId) => {
    if (!auth.currentUser || !orgMember || (orgMember.role !== 'admin' && orgMember.role !== 'Administrator')) throw new Error("Must be an org admin");
    const memberId = `${userId}_${orgMember.orgId}`;
    await updateDoc(doc(db, 'org_members', memberId), {
      teamIds: arrayRemove(teamId)
    });
  };

  const deleteTeam = async (teamId) => {
    if (!auth.currentUser || !orgMember || (orgMember.role !== 'admin' && orgMember.role !== 'Administrator')) throw new Error("Must be an org admin");
    
    // First remove teamId from all org members
    const membersQ = query(collection(db, 'org_members'), where('orgId', '==', orgMember.orgId));
    const membersSnap = await getDocs(membersQ);
    
    const updatePromises = [];
    membersSnap.forEach((memberDoc) => {
      const data = memberDoc.data();
      if (data.teamIds && data.teamIds.includes(teamId)) {
        updatePromises.push(updateDoc(doc(db, 'org_members', memberDoc.id), {
          teamIds: arrayRemove(teamId)
        }));
      }
    });
    
    await Promise.all(updatePromises);
    
    // Delete the team document
    await deleteDoc(doc(db, 'teams', teamId));
  };

  const addTeamMember = async (teamId, email, role) => {
    if (!auth.currentUser || !orgMember) throw new Error("Must be logged in");
    await addDoc(collection(db, 'team_requests'), {
      teamId,
      orgId: orgMember.orgId,
      userEmail: email,
      role: role || 'viewer',
      status: 'pending',
      createdAt: new Date().toISOString()
    });
  };

  const approveJoinRequest = async (teamId, requestId, userId, userEmail) => {
    if (!auth.currentUser || !orgMember || (orgMember.role !== 'admin' && orgMember.role !== 'Administrator')) throw new Error("Must be an org admin");
    
    // Assuming userId exists for the request. If not, they have to sign up first.
    if (userId) {
      const memberId = `${userId}_${orgMember.orgId}`;
      await updateDoc(doc(db, 'org_members', memberId), {
        teamIds: arrayUnion(teamId)
      });
    }
    
    await updateDoc(doc(db, 'team_requests', requestId), {
      status: 'approved',
      handledAt: new Date().toISOString()
    });
  };
  
  const setTeamRole = async (teamId, userId, newRole) => {
    if (!auth.currentUser || !orgMember || (orgMember.role !== 'admin' && orgMember.role !== 'Administrator')) throw new Error("Must be an org admin");
    const memberId = `${userId}_${orgMember.orgId}`;
    
    // Store team roles in a map like: teamRoles: { teamId1: 'lead', teamId2: 'member' }
    const updatePath = `teamRoles.${teamId}`;
    await updateDoc(doc(db, 'org_members', memberId), {
      [updatePath]: newRole
    });
  };

  return { teams, currentTeam, joinRequests, loading, createTeam, addUserToTeam, removeUserFromTeam, deleteTeam, addTeamMember, approveJoinRequest, setTeamRole };
}
